/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack, Alert } from 'react-bootstrap';
import { getSetting, setSetting } from '../../data';
import { MetadataContext } from '../MetadataContext';
import { SettingsContext } from '../SettingsContext';
import { StatusContext } from '../StatusContext';
import { useTheme } from 'styled-components';

function OpsSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const metadataContext = useContext<any>(MetadataContext);
    const settingsContext = useContext(SettingsContext);
    const statusContext = useContext(StatusContext);
    const [refresh, setRefresh] = useState(0);

    const [accessKeyId,     setAccessKeyId]     = useState('');
    const [secretAccessKey, setSecretAccessKey] = useState('');
    const [region,          setRegion]          = useState('');

    const [origAccessKeyId,     setOrigAccessKeyId]     = useState('');
    const [origSecretAccessKey, setOrigSecretAccessKey] = useState('');
    const [origRegion,          setOrigRegion]          = useState('');

    const [showSecret, setShowSecret] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);

    const dirty = accessKeyId !== origAccessKeyId
        || secretAccessKey !== origSecretAccessKey
        || region !== origRegion;

    useEffect(() => { settingsContext.setIsDirty(dirty); }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const id  = await getSetting('/aws/access_key_id');
                const sec = await getSetting('/aws/secret_access_key');
                const reg = await getSetting('/aws/region');
                setAccessKeyId(id || '');         setOrigAccessKeyId(id || '');
                setSecretAccessKey(sec || '');    setOrigSecretAccessKey(sec || '');
                setRegion(reg || '');             setOrigRegion(reg || '');
            } catch (err: any) {
                console.error('Error loading ops settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        if (isSaving) {
            return;
        }

        setIsSaving(true);
        setSaveError('');
        setSaveSuccess(false);

        try {
            await setSetting('/aws/access_key_id',     accessKeyId.trim(),     'AWS credential');
            await setSetting('/aws/secret_access_key', secretAccessKey.trim(), 'AWS secret credential');
            await setSetting('/aws/region',            region.trim(),          'AWS region');
            metadataContext.refresh();
            setRefresh(r => r + 1);
            setSaveSuccess(true);
            statusContext.postInfo('Ops settings saved.');
        } catch (err: any) {
            const msg = err?.message || String(err) || 'Failed to save ops settings';
            setSaveError(msg);
            statusContext.postError(`Failed to save ops settings: ${msg}`);
        } finally {
            setIsSaving(false);
        }
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem',
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4"
                style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Ops</h3>
                    <div className="text-muted">AWS credentials for cloud operations and terraform access.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" type="button" disabled={isSaving} onClick={() => {
                            setSaveError('');
                            setSaveSuccess(false);
                            setAccessKeyId(origAccessKeyId);
                            setSecretAccessKey(origSecretAccessKey);
                            setRegion(origRegion);
                        }}>Clear</Button>
                        <Button variant="primary" type="button" disabled={isSaving} onClick={() => void save()}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Stack>
                )}
            </div>

            {saveError && (
                <Alert variant="danger" dismissible onClose={() => setSaveError('')}>
                    {saveError}
                </Alert>
            )}
            {saveSuccess && !saveError && (
                <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)}>
                    Ops settings saved.
                </Alert>
            )}

            <div style={{ borderTop: sectionBorder, paddingTop: '1rem', marginTop: '1rem' }}>
                <h5 className="mb-3">AWS Credentials</h5>

                <div className="mb-4" style={changedStyle(accessKeyId !== origAccessKeyId)}>
                    <Form.Label htmlFor="awsAccessKeyId" className="fw-semibold">Access Key ID</Form.Label>
                    <div className="text-muted small mb-2">AWS IAM access key ID.</div>
                    <Form.Control
                        id="awsAccessKeyId"
                        value={accessKeyId}
                        onChange={e => setAccessKeyId(e.target.value)}
                        type="text"
                        autoComplete="off"
                        style={{ maxWidth: '28rem', fontFamily: 'monospace' }}
                        placeholder="AKIAIOSFODNN7EXAMPLE"
                    />
                </div>

                <div className="mb-4" style={changedStyle(secretAccessKey !== origSecretAccessKey)}>
                    <Form.Label htmlFor="awsSecretAccessKey" className="fw-semibold">Secret Access Key</Form.Label>
                    <div className="text-muted small mb-2">AWS IAM secret access key.</div>
                    <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                            id="awsSecretAccessKey"
                            value={secretAccessKey}
                            onChange={e => setSecretAccessKey(e.target.value)}
                            type={showSecret ? 'text' : 'password'}
                            autoComplete="new-password"
                            style={{ maxWidth: '28rem', fontFamily: 'monospace' }}
                            placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                        />
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowSecret(s => !s)}
                        >
                            {showSecret ? 'Hide' : 'Show'}
                        </Button>
                    </div>
                </div>

                <div className="mb-4" style={changedStyle(region !== origRegion)}>
                    <Form.Label htmlFor="awsRegion" className="fw-semibold">Region</Form.Label>
                    <div className="text-muted small mb-2">AWS region for S3 and other service calls.</div>
                    <Form.Control
                        id="awsRegion"
                        value={region}
                        onChange={e => setRegion(e.target.value)}
                        type="text"
                        style={{ maxWidth: '16rem', fontFamily: 'monospace' }}
                        placeholder="us-west-2"
                    />
                </div>
            </div>
        </div>
    );
}

export default OpsSettings;
